import { validateShipmentRow } from "../../utils/shipmentValidation";
import { fixationMatchesContainer } from "../../utils/fixationAllocation";
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "../../utils/axiosConfig";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import DealNavigation from "../../components/DealNavigation";
import BuyerDetailModal from "../../components/BuyerDetailModal";
import ShipperDetailModal from "../../components/ShipperDetailModal";
import FollowupModal from "../../components/FollowupModal";
import FixationModal from "../../components/FixationModal";
import DealInfoPanel from "../../components/DealInfoPanel";
import { buildFixationPricingDefaults } from "../../utils/fixationDefaults";
import { calculateFixedShipmentInvoice } from "../../utils/shipmentInvoice";
import { fillMissingVesselDetails, updateVesselDetails, readShipmentDocumentFields, SHARED_VESSEL_FIELDS } from "../../utils/shipmentVesselDetails";
import { containerAdvance, getShipmentTotals, getAdvanceAllocation, buildBLInvoices, blVesselKey, applyBLAdvance, normalizeBLAdvances, previewBLRows } from "../../utils/advanceAllocation";
import { showToast } from "../../utils/toast";
import { buildCourierGroups } from "../../utils/shipmentCourier";
import "./ShipmentSchedule.css";

const formatDate = (value) => (value ? String(value).split("T")[0] : "");
const CONTAINER_NO_MAX_LENGTH = 11;
const SHIP_LINE_OTHER = "__OTHER__";
const SHIP_LINE_OPTIONS = [
    "ACL", "Dalreftrans", "Interport", "Oceanic Star Line", "Swire Shipping North America",
    "Admiral Container Lines", "DB Schenker", "IRIS Lines", "Oceanus Container Lines", "T.S. Lines",
    "Africa Express Line", "DHL", "Italia Marittima", "ONE", "TAILWIND Shipping Lines",
    "Aladin Express", "Dong Young Shipping", "K-Apex", "OOCL", "Tanto", "Allalouf",
    "Dongjin Shipping", "Kambara Kisen", "Pan Continental Shipping", "Tarros", "ANL",
    "Econ Shipping", "Kanway Line", "Pan Ocean", "TCI Seaways", "APL", "ECU Worldwide",
    "KiwiRail", "Pasha Hawaii", "Textainer", "Arkas Line", "Ellerman", "Korea Marine Transport",
    "PIL", "TIS-logistic", "Asyad Line", "Emirates Shipping Line", "Kuehne + Nagel",
    "PSL Navegação", "Topocean", "Avana Logistek", "Emkay Line", "Laurel Navigation",
    "Qatar Navigation", "TOTE Maritime", "BAL", "ESL", "Leschaco", "RCL", "Touax",
    "Balticon", "Evergreen", "LX Pantos", "Romocean", "Trailer Bridge", "Bermuda Container Line",
    "Expressway", "Macnels", "Royal Cargo", "Trans Asia", "BLPL Singapore", "Fairfreight",
    "MACS", "Samskip", "TransContainer", "Blue Sky", "FESCO", "Maersk Line",
    "Samudera Shipping", "Transmar", "Blue Water Lines", "Finnlines", "Maldives Ports",
    "Sarjak Container Lines", "Transvision Shipping", "Bridgehead", "FlexiVan", "Marfret",
    "SCI", "Triton International", "Brointermed Lines", "GCL", "Margarita Shipping",
    "Sea Hawk Lines", "Turkon Line", "CAI", "Gold Star Line", "Marguisa", "Seaboard Marine",
    "UES", "Camellia Line", "Goodrich", "Mariana Express Lines", "Seaco", "Unifeeder",
    "Canadian National Railway", "Grimaldi", "Matson", "SeaCube", "UWL",
    "Canadian Pacific Railway", "GS Lines", "Maxicon Container Line", "Sealand",
    "Vertex Container Line", "Carpenters Shipping", "Haian", "Mediterranean Shipping",
    "SETH Shipping", "Volta", "CARU containers", "Hapag-Lloyd", "MEDKON LINES",
    "Sidra Line", "W.E.C. Lines", "CCIS", "Hecny Group", "Meratus Line",
    "Silkargo Logistics", "Wan Hai Lines", "CMA CGM", "Hede Shipping", "Messina Line",
    "Silmar Shipping", "White Line", "CNC", "Heung-A Line", "Minsheng Ocean Shipping",
    "Sinokor Merchant Marine", "World Direct Shipping", "CONCOR", "Hikaru Shipping Line",
    "Namsung", "Sinotrans", "World Transport Overseas", "Consort Express Lines",
    "Hillebrand Gori", "National Shipping of America", "SITC", "WorldWide Alliance",
    "Cordelia Container Shipping Line", "HMM", "Nauka Lines", "SM Line",
    "Xi 'an International", "COSCO SHIPPING Lines", "HS LINE", "NCL", "Sofrana ANL",
    "Yang Ming", "Crowley", "Independent Container Line", "Neptune Logistics", "SPIL",
    "YXE", "CS Leasing", "Indo Container Lines", "Nirint Shipping", "STG Logistics",
    "ZIM", "CULines", "Inox Shipping", "NPDL", "Sunmarine", "Zim World Freight",
    "DahNay Logistics", "Interasia Lines", "Ocean Axis", "Swire Shipping"
];
const SORTED_SHIP_LINE_OPTIONS = [...SHIP_LINE_OPTIONS].sort((left, right) =>
    left.localeCompare(right, undefined, { sensitivity: "base" })
);

const emptyShipmentForm = (defaults = {}) => ({
    recDate: "",
    containerNo: "",
    netWeight: "",
    fixationId: "",
    shipLine: "",
    copyDocReceiveDate: "",
    vesselName: "",
    etd: "",
    eta: "",
    blNo: defaults.blNo || "",
    blDate: defaults.blDate || "",
    advancePayment: defaults.advancePayment || 0,
    balanceAdvance: defaults.balanceAdvance || 0,
    invoiceNo: defaults.invoiceNo || "",
    invoiceDate: defaults.invoiceDate || "",
    invoiceAmount: defaults.invoiceAmount || 0,
    invoiceAmountFromFixation: false,
    balanceAmount: defaults.balanceAmount || 0,
    freeDetentionDays: defaults.freeDetentionDays || "",
    showItemDetails: defaults.showItemDetails || false,
    itemDetails: defaults.itemDetails || []
});

const withBalanceAmount = (nextForm) => ({
    ...nextForm,
    balanceAmount: Number(nextForm.invoiceAmount || 0) - Number(nextForm.advancePayment || 0)
});

const getItemId = (item) => item?.ItemID ?? item?.ItemId ?? item?.itemId ?? "";
const getDealItemId = (item) => item?.DealItemID ?? item?.dealItemId ?? "";
const getItemName = (item) => item?.ItemName ?? item?.itemName ?? item?.ItemCode ?? item?.itemCode ?? "";
const getItemCode = (item) => item?.ItemCode ?? item?.itemCode ?? "";
const getItemQuantity = (item) => item?.Quantity ?? item?.quantity ?? "";
const roundMoney = (value) => Number(Number(value || 0).toFixed(2));
const ShipmentSchedule = () => {
    const { dealId } = useParams();
    const navigate = useNavigate();
    const [deal, setDeal] = useState({});
    const [totalPaidAdvance, setTotalPaidAdvance] = useState(0);
    // const [containers, setContainers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showDealInfo, setShowDealInfo] = useState(true);
    const [showShipment, setShowShipment] = useState(true);
    const [showDocument, setShowDocument] = useState(true);
    const [showCourier, setShowCourier] = useState(true);


    const [documentData, setDocumentData] = useState({});
    const [courierDetails, setCourierDetails] = useState([]);
    const [legacyCourier, setLegacyCourier] = useState({});

    const [fixations, setFixations] = useState([]);
    const [dealItems, setDealItems] = useState([]);
    const [shipmentRows, setShipmentRows] = useState([]);
    const deletingContainerRef = useRef(false);
    const [selectedContainerNos, setSelectedContainerNos] = useState([]);
    const [editingIndex, setEditingIndex] = useState(null);
    const [vesselEditingIndex, setVesselEditingIndex] = useState(null);
    const [showModal, setShowModal] = useState(false)
    const [showContainerModal, setShowContainerModal] = useState(false);
    // Number of shipmentRows that already existed when the Container Details
    // modal was opened — its grid only shows rows added after that point,
    // i.e. containers added during the current modal session.
    const [containerModalBaseCount, setContainerModalBaseCount] = useState(0);
    const openContainerModal = () => {
        if (!String(form.vesselName || "").trim()) {
            showToast("Vessel Name is required");
            return;
        }
        setContainerModalBaseCount(shipmentRows.length);
        setShowContainerModal(true);
    };
    // Closing the container session clears the editor; staged rows remain in the grid.
    const resetShipmentEditor = () => {
        setForm(emptyShipmentForm());
        setEditingIndex(null);
        setVesselEditingIndex(null);
        setIsOtherShipLine(false);
        setSelectedContainerNos([]);
        setShowContainerModal(false);
        setContainerModalBaseCount(0);
    };
    const closeContainerModal = () => resetShipmentEditor();
    const handleCancel = async () => {
        // Restore saved rows so Cancel also discards staged additions and edits.
        await fetchData();
        resetShipmentEditor();
        setDocForm({
            blNo: "", blDate: "", invoiceNo: "", invoiceDate: "",
            invoiceAmount: 0, advancePayment: 0, balanceAdvance: 0,
            balanceAmount: 0, freeDetentionDays: ""
        });
    };
    const [selectedShipment, setSelectedShipment] = useState(null)
    const [showBuyer, setShowBuyer] = useState(false);
    const [showShipper, setShowShipper] = useState(false);
    const [showFollowup, setShowFollowup] = useState(false);
    const [showFixation, setShowFixation] = useState(false);
    const [form, setForm] = useState(emptyShipmentForm());
    const shipmentFormRef = useRef(null);
    const advanceAllocation = getAdvanceAllocation(totalPaidAdvance, shipmentRows, editingIndex, form);
    const containerLimit = Number(deal.NoOfContainer);
    const hasReachedContainerLimit = Number.isInteger(containerLimit) &&
        containerLimit > 0 && shipmentRows.length >= containerLimit;
    const contractQuantity = Number(deal.TotalQty);
    const scheduledQuantity = shipmentRows.reduce(
        (total, row) => total + Number(row.netWeight || 0),
        0
    );
    const [isOtherShipLine, setIsOtherShipLine] = useState(false);
    const isKnownShipLine = (value) => SHIP_LINE_OPTIONS.includes(value);
    const shipLineSelectValue = isOtherShipLine
        ? SHIP_LINE_OTHER
        : form.shipLine && isKnownShipLine(form.shipLine)
        ? form.shipLine
        : form.shipLine
            ? SHIP_LINE_OTHER
            : "";
    const handleShipLineSelect = (value) => {
        if (value === SHIP_LINE_OTHER) {
            setIsOtherShipLine(true);
            setForm((prev) => ({ ...prev, shipLine: "" }));
            return;
        }

        setIsOtherShipLine(false);
        setForm((prev) => ({
            ...prev,
            shipLine: value
        }));
    };

    const [docForm, setDocForm] = useState({
        blNo: "",
        blDate: "",
        advancePayment: 0,
        balanceAdvance: 0,
        invoiceNo: "",
        invoiceDate: "",
        invoiceAmount: 0,
        balanceAmount: 0,
        freeDetentionDays: ""
    });

    const courierGroups = useMemo(
        () => buildCourierGroups(shipmentRows, courierDetails, legacyCourier),
        [shipmentRows, courierDetails, legacyCourier]
    );
    const updateCourierDetail = (group, field, value) => {
        setCourierDetails((current) => {
            const existingIndex = current.findIndex((detail) => detail.key === group.key);
            const nextDetail = { ...group, ...(existingIndex >= 0 ? current[existingIndex] : {}), key: group.key, [field]: value };
            if (existingIndex < 0) return [...current, nextDetail];
            return current.map((detail, index) => index === existingIndex ? nextDetail : detail);
        });
        setLegacyCourier({});
    };
    const fixationPricingDefaults = useMemo(() => buildFixationPricingDefaults(deal, dealItems), [deal, dealItems]);
    const fixedInvoiceAmount = useMemo(() => calculateFixedShipmentInvoice(
        form.itemDetails, dealItems
    ), [form.itemDetails, dealItems]);

    useEffect(() => {
        if (fixedInvoiceAmount === null) return;
        setForm(current => withBalanceAmount({
            ...current,
            invoiceAmount: fixedInvoiceAmount.toFixed(2),
            invoiceAmountFromFixation: false
        }));
    }, [fixedInvoiceAmount, form.itemDetails, form.advancePayment]);
    const fixationWorksheetRows = useMemo(() => {
        const hasCurrentShipmentItems = form.containerNo && Array.isArray(form.itemDetails) && form.itemDetails.length > 0;
        const currentShipmentRow = hasCurrentShipmentItems
            ? {
                ...form,
                netWeight: form.netWeight || form.itemDetails.reduce((sum, item) => sum + (Number(item.netWeight || 0) || 0), 0).toFixed(3)
            }
            : null;

        if (editingIndex !== null) {
            return applyBLAdvance(shipmentRows.map((row, index) => (
                index === editingIndex && currentShipmentRow
                    ? { ...row, ...currentShipmentRow, ShipmentID: row.ShipmentID }
                    : row
            )), form);
        }

        return currentShipmentRow
            ? [...shipmentRows, currentShipmentRow]
            : shipmentRows;
    }, [editingIndex, form, shipmentRows]);
    const getFixationLabel = (fixationId, fallback = "", sourceFixations = fixations) => {
        const selectedFixation = sourceFixations.find(
            f => String(f.FixationID) === String(fixationId)
        );

        return selectedFixation
            ? `${selectedFixation.Fixation} | ${selectedFixation.Currency || deal.DealCurrency || "USD"} ${Number(selectedFixation.FinalPrice || 0).toFixed(2)}`
            : fallback;
    };

    const getContainerFixation = (shipment, sourceFixations = fixations) => sourceFixations.find((fixation) =>
        fixationMatchesContainer(fixation, shipment || {}) ||
        (fixation.ContainerNo && String(fixation.ContainerNo) === String(shipment?.ContainerNo || shipment?.containerNo || ""))
    );

    const calculateFixationInvoiceAmount = (row, containerFixation) => {
        if (!containerFixation) return null;

        const fixedLme = roundMoney(
            containerFixation.FixationValue ||
            containerFixation.LMEPrice ||
            containerFixation.FinalPrice
        );

        return roundMoney((row.itemDetails || []).reduce((total, shipmentItem) => {
            const dealItem = dealItems.find((item) =>
                String(getDealItemId(item) || "") === String(shipmentItem.dealItemId || "") ||
                String(getItemId(item) || "") === String(shipmentItem.itemId || "")
            ) || {};
            // PriceAdjustmentValue = "Per Of LME" (always a percentage of LME).
            // PriceAdjustmentType = "Disc&Prem" (a flat +/- amount added on top).
            const perOfLme = Number(dealItem.PriceAdjustmentValue || 0);
            const discPrem = Number(dealItem.PriceAdjustmentType || 0);
            const netWeight = Number(shipmentItem.netWeight || 0);
            const itemPrice = roundMoney((fixedLme * perOfLme / 100) + discPrem);

            return total + roundMoney(itemPrice * netWeight);
        }, 0));
    };

    const getShipmentInvoiceAmount = (row) => {
        const fixedAmount = calculateFixedShipmentInvoice(row.itemDetails || [], dealItems);
        if (fixedAmount !== null) return fixedAmount;

        return calculateFixationInvoiceAmount(row, getContainerFixation(row)) ?? Number(row.invoiceAmount || 0);
    };

    const gridRows = normalizeBLAdvances(shipmentRows.map(row => ({
        ...row, invoiceAmount: getShipmentInvoiceAmount(row)
    })));
    const gridTotals = getShipmentTotals(gridRows);
    const previewRows = previewBLRows(shipmentRows, editingIndex, form);
    const currentBLInvoice = blVesselKey(form)
        ? buildBLInvoices(previewRows.filter(row => row.containerNo).map(row => ({ ...row, invoiceAmount: getShipmentInvoiceAmount(row) })))
            .find(group => group.key === blVesselKey(form))
        : null;

    const createItemDetailRows = (sourceItems = dealItems, existingDetails = [], sourceFixations = fixations, defaultFixationId = "") => {
        return sourceItems.map((item) => {
            const dealItemId = getDealItemId(item);
            const itemId = getItemId(item);
            const existing = existingDetails.find(detail =>
                String(detail.DealItemID || detail.dealItemId || "") === String(dealItemId || "") ||
                String(detail.ItemID || detail.itemId || "") === String(itemId || "")
            );

            const fixationId = existing?.FixationID ?? existing?.fixationId ?? defaultFixationId;

            return {
                shipmentItemId: existing?.ShipmentItemID || existing?.shipmentItemId || null,
                dealItemId,
                itemId,
                itemName: existing?.ItemName || existing?.itemName || getItemName(item),
                itemCode: existing?.ItemCode || existing?.itemCode || getItemCode(item),
                quantity: existing?.Quantity ?? existing?.quantity ?? getItemQuantity(item),
                netWeight: existing?.NetWeight ?? existing?.netWeight ?? "",
                fixationId: fixationId || "",
                fixation: getFixationLabel(fixationId, existing?.Fixation || existing?.fixation || "", sourceFixations)
            };
        });
    };

    const getItemDetailNetWeightTotal = (itemDetails = []) => (
        itemDetails.reduce((sum, item) => sum + (Number(item.netWeight || 0) || 0), 0)
    );

    const loadFixations = async () => {
        try {
            const res = await axios.get(`/shipment/fixations/${dealId}`);
            console.log(res.data);
            const rows = Array.isArray(res.data) ? res.data : [];
            setFixations(rows);
            return rows;
        } catch (err) {
            console.error(err);
            setFixations([]);
            return [];
        }
    };

    useEffect(() => {
        if (dealId) {
            fetchData();
        }
    }, [dealId]);

    const fetchData = async () => {

        try {

            setLoading(true);

            console.log("Loading deal:", dealId);

            const dealRes = await axios.get(`/container-release/deal/${dealId}`);
            console.log("Deal response:", dealRes.data);

            const shipmentRes = await axios.get(`/shipment/${dealId}`);
            console.log("Shipment response:", shipmentRes.data);

            const loadedFixations = await loadFixations();
            const dealDetailRes = await axios.get(`/newdeal/${dealId}`);
            const loadedDealItems = Array.isArray(dealDetailRes.data?.items)
                ? dealDetailRes.data.items
                : [];
            setDealItems(loadedDealItems);

            setDeal(dealRes.data || {});

            const shipments = shipmentRes.data?.shipment || [];
            const savedItemDetails = Array.isArray(shipmentRes.data?.itemDetails)
                ? shipmentRes.data.itemDetails
                : [];
            const document = shipmentRes.data?.document || {};
            const courier = shipmentRes.data?.courier || {};
            const savedCourierDetails = Array.isArray(shipmentRes.data?.courierDetails)
                ? shipmentRes.data.courierDetails
                : [];
            const advanceSummary = shipmentRes.data?.advanceSummary || {};
            const savedAdvance = Number(document.AdvancePayment || 0);
            const paidAdvance = Number(advanceSummary.paidAdvance || 0);
            setTotalPaidAdvance(paidAdvance);
            const savedBalanceAdvance = Number(document.BalanceAdvance || 0);
            const pendingAdvance = Number(advanceSummary.balanceAdvance || 0);

            const formattedShipments = shipments.map(s => {
                const shipmentItemDetails = savedItemDetails.filter(detail =>
                    String(detail.ShipmentID || "") === String(s.ShipmentID || "") ||
                    (
                        !detail.ShipmentID &&
                        String(detail.ContainerNo || "") === String(s.ContainerNo || "")
                    )
                );
                const containerFixation = getContainerFixation(s, loadedFixations);
                const fixationId = s.FixationID || containerFixation?.FixationID || "";

                return {
                    ShipmentID: s.ShipmentID,
                    recDate: formatDate(s.RecDate),
                    containerNo: s.ContainerNo || "",
                    netWeight: s.NetWeight || "",
                    fixationId,
                    fixation: getFixationLabel(fixationId, "", loadedFixations),
                    finalPrice: containerFixation?.FinalPrice ?? s.FinalPrice ?? "",
                    shipLine: s.ShipLine || "",
                    copyDocReceiveDate: formatDate(s.CopyDocReceiveDate),
                    vesselName: s.VesselName || "",
                    etd: formatDate(s.ETD),
                    eta: formatDate(s.ETA),
                    ...readShipmentDocumentFields(s, formatDate),
                    balanceAdvance: s.BalanceAdvance ?? (savedBalanceAdvance || pendingAdvance || 0),
                    itemDetails: createItemDetailRows(loadedDealItems, shipmentItemDetails, loadedFixations, fixationId),
                    showItemDetails: shipmentItemDetails.length > 0
                };
            });

            setShipmentRows(normalizeBLAdvances(fillMissingVesselDetails(formattedShipments)));
            setSelectedContainerNos([]);

            const nextDocForm = {
                blNo: document.BLNo || "",
                blDate: formatDate(document.BLDate),
                invoiceNo: document.InvoiceNo || "",
                invoiceDate: formatDate(document.InvoiceDate),
                invoiceAmount: document.InvoiceAmount || 0,
                advancePayment: savedAdvance || paidAdvance || 0,
                balanceAdvance: savedBalanceAdvance || pendingAdvance || 0,
                balanceAmount: document.BalanceAmount || 0,
                freeDetentionDays: document.FreeDetentionDays || ""
            };

            setDocForm(nextDocForm);
            setForm(prev => prev.containerNo ? prev : emptyShipmentForm({
                advancePayment: 0,
                balanceAdvance: nextDocForm.balanceAdvance
            }));

            setCourierDetails(savedCourierDetails.map((detail) => ({
                key: detail.BLNo
                    ? `bl:${String(detail.BLNo).trim().toLocaleUpperCase()}`
                    : `vessel:${String(detail.VesselName || "UNASSIGNED").trim().toLocaleUpperCase()}`,
                courierDetailId: detail.CourierDetailID || null,
                vesselName: detail.VesselName || "",
                blNo: detail.BLNo || "",
                courierDate: formatDate(detail.CourierDate),
                refNo: detail.RefNo || "",
                docReceiveDate: formatDate(detail.DocRecDate)
            })));
            setLegacyCourier(savedCourierDetails.length ? {} : courier);

        } catch (err) {

            console.error("FETCH ERROR:", err);

            if (err.response) {
                console.error("API Error:", err.response.data);
                const message = err.response.data?.message || err.response.data || "API Error";
                showToast(message);
                if (err.response.status === 400 && String(message).toLowerCase().includes("advance payment")) {
                    navigate(`/transaction/advance-payment/${dealId}`);
                }
            } else {
                showToast("Server not reachable");
            }

        }

        setLoading(false);
    };
    const normalizeItemDetailsForSave = (itemDetails = []) => (
        itemDetails
            .filter(item => item.dealItemId || item.itemId || item.itemName || item.netWeight || item.fixationId)
            .map(item => ({
                dealItemId: item.dealItemId || null,
                itemId: item.itemId || null,
                itemName: item.itemName || "",
                itemCode: item.itemCode || "",
                quantity: item.quantity || null,
                netWeight: item.netWeight || null,
                fixationId: item.fixationId || null
            }))
    );

    const isContainerSelected = (row) => selectedContainerNos.includes(String(row.containerNo));
    const toggleContainerSelection = (containerNo) => {
        const key = String(containerNo);
        setSelectedContainerNos((current) => current.includes(key)
            ? current.filter((value) => value !== key)
            : [...current, key]);
    };
    const toggleAllContainers = () => {
        const allKeys = shipmentRows.map((row) => String(row.containerNo));
        setSelectedContainerNos((current) => current.length === allKeys.length ? [] : allKeys);
    };
    const applyToSelectedContainers = (fields, description) => {
        if (!selectedContainerNos.length) {
            showToast(`Select at least one container before applying ${description}.`);
            return;
        }
        setShipmentRows((rows) => rows.map((row) => (
            isContainerSelected(row)
                ? { ...row, ...Object.fromEntries(fields.map((field) => [field, form[field]])) }
                : row
        )));
        showToast(`${description} applied to ${selectedContainerNos.length} selected container(s).`);
    };

    const applyScheduleToSelected = () => applyToSelectedContainers(
        ["vesselName", "shipLine", "etd", "eta"],
        "vessel and schedule"
    );
    const applyDocumentToSelected = () => applyToSelectedContainers(
        ["blNo", "blDate", "invoiceNo", "invoiceDate", "invoiceAmount", "advancePayment", "balanceAdvance", "balanceAmount", "freeDetentionDays"],
        "container document and payment"
    );

    const getFormShipmentRow = () => {
        if (!String(form.vesselName || "").trim()) {
            showToast("Vessel Name is required");
            return null;
        }

        if (editingIndex === null) return null;

        const itemDetailNetWeight = getItemDetailNetWeightTotal(form.itemDetails);
        const netWeight = form.netWeight || (itemDetailNetWeight ? itemDetailNetWeight.toFixed(3) : "");

        if (!form.containerNo) {
            showToast("Container No required");
            return null;
        }

        if (!netWeight) {
            showToast("Net Weight required");
            return null;
        }

        const selectedFixation = fixations.find(
            f => String(f.FixationID) === String(form.fixationId)
        );
        const fixationName = selectedFixation
            ? `${selectedFixation.Fixation} | ${selectedFixation.Currency || deal.DealCurrency || "USD"} ${Number(selectedFixation.FinalPrice || 0).toFixed(2)}`
            : shipmentRows[editingIndex]?.fixation || "";

        return {
            ...shipmentRows[editingIndex],
            ...form,
            netWeight,
            fixation: fixationName,
            finalPrice: selectedFixation?.FinalPrice || shipmentRows[editingIndex]?.finalPrice || ""
        };
    };

    const getNewShipmentRow = () => {
        if (!String(form.vesselName || "").trim()) {
            showToast("Vessel Name is required");
            return null;
        }

        if (!form.containerNo) {
            showToast("Container No required");
            return null;
        }

        const itemDetailNetWeight = getItemDetailNetWeightTotal(form.itemDetails);
        const netWeight = form.netWeight || (itemDetailNetWeight ? itemDetailNetWeight.toFixed(3) : "");

        if (!netWeight) {
            showToast("Net Weight required");
            return null;
        }

        const selectedFixation = fixations.find(
            f => String(f.FixationID) === String(form.fixationId)
        );
        const fixationName = selectedFixation
            ? `${selectedFixation.Fixation} | ${selectedFixation.Currency || deal.DealCurrency || "USD"} ${Number(selectedFixation.FinalPrice || 0).toFixed(2)}`
            : "";
        const inheritedForm = editingIndex === null
            ? fillMissingVesselDetails([form, ...shipmentRows])[0]
            : form;
        return {
            ...inheritedForm,
            netWeight,
            fixation: fixationName,
            finalPrice: selectedFixation?.FinalPrice || "",
            ShipmentID: null
        };
    };

    const getShipmentRowsForSave = () => {
        if (editingIndex === null) {
            if (vesselEditingIndex === null || !shipmentRows[vesselEditingIndex]) return shipmentRows;
            const updatedRow = {
                ...shipmentRows[vesselEditingIndex],
                ...Object.fromEntries(SHARED_VESSEL_FIELDS.map(field => [field, form[field]]))
            };
            return updateVesselDetails(shipmentRows, vesselEditingIndex, updatedRow);
        }

        const editedRow = getFormShipmentRow();
        if (!editedRow) return null;

        return updateVesselDetails(shipmentRows, editingIndex, editedRow);
    };

    const handleSave = async (rowsOverride = null, documentOverride = null) => {

        try {

            const pendingRows = rowsOverride || getShipmentRowsForSave();
            if (!pendingRows) return;
            if (!pendingRows.length) {
                showToast(!String(form.vesselName || "").trim()
                    ? "Vessel Name is required"
                    : "Add at least one container before saving");
                return false;
            }
            const rowsForSave = normalizeBLAdvances(fillMissingVesselDetails(pendingRows).map(row => ({ ...row, invoiceAmount: getShipmentInvoiceAmount(row) })));
            const validationError = rowsForSave.map(validateShipmentRow).find(Boolean);
            if (validationError) { showToast(validationError); return false; }
            const missingVessel = rowsForSave.find((row) => !String(row.vesselName || "").trim());
            if (missingVessel) {
                showToast(`Vessel Name is required for container ${missingVessel.containerNo}`);
                return false;
            }

            const containers = rowsForSave.map(r => ({
                itemDetails: normalizeItemDetailsForSave(r.itemDetails),
                ShipmentID: r.ShipmentID || null,
                recDate: r.recDate || null,
                containerNo: r.containerNo,
                netWeight: Number(r.netWeight || getItemDetailNetWeightTotal(r.itemDetails) || 0),
                fixationId: r.fixationId || null,
                shipLine: r.shipLine || "",
                copyDocReceiveDate: r.copyDocReceiveDate || null,
                vesselName: r.vesselName || "",
                etd: r.etd || null,
                eta: r.eta || null,
                blNo: r.blNo || "",
                blDate: r.blDate || null,
                advancePayment: Number(r.advancePayment || 0),
                balanceAdvance: Number(r.balanceAdvance || 0),
                invoiceNo: r.invoiceNo || "",
                invoiceDate: r.invoiceDate || null,
                invoiceAmount: Number(r.invoiceAmount || 0),
                balanceAmount: Number(r.balanceAmount || 0),
                freeDetentionDays: r.freeDetentionDays || null
            }));
            const firstDocumentRow = rowsForSave.find(
                r => r.blNo || r.blDate || r.invoiceNo || r.invoiceDate
            ) || {};

            const documentPayload = documentOverride
                ? {
                    blNo: documentOverride.blNo || "",
                    blDate: documentOverride.blDate || null,
                    invoiceNo: documentOverride.invoiceNo || "",
                    invoiceDate: documentOverride.invoiceDate || null,
                    invoiceAmount: Number(documentOverride.invoiceAmount || 0),
                    advancePayment: Number(documentOverride.advancePayment || 0),
                    balanceAdvance: Number(documentOverride.balanceAdvance || 0),
                    balanceAmount: Number(documentOverride.balanceAmount || 0),
                    freeDetentionDays: documentOverride.freeDetentionDays || null
                }
                : {
                    blNo: docForm.blNo || firstDocumentRow.blNo || "",
                    blDate: docForm.blDate || firstDocumentRow.blDate || null,
                    invoiceNo: docForm.invoiceNo || firstDocumentRow.invoiceNo || "",
                    invoiceDate: docForm.invoiceDate || firstDocumentRow.invoiceDate || null,
                    invoiceAmount: Number(docForm.invoiceAmount || firstDocumentRow.invoiceAmount || 0),
                    advancePayment: Number(docForm.advancePayment || firstDocumentRow.advancePayment || 0),
                    balanceAdvance: Number(docForm.balanceAdvance || firstDocumentRow.balanceAdvance || 0),
                    balanceAmount: Number(docForm.balanceAmount || firstDocumentRow.balanceAmount || 0),
                    freeDetentionDays: docForm.freeDetentionDays || firstDocumentRow.freeDetentionDays || null
                };

            const groupedInvoices = buildBLInvoices(rowsForSave);
            documentPayload.invoiceAmount = roundMoney(groupedInvoices.reduce((sum, group) => sum + group.invoiceAmount, 0));
            documentPayload.advancePayment = roundMoney(groupedInvoices.reduce((sum, group) => sum + group.advancePayment, 0));
            documentPayload.balanceAmount = roundMoney(documentPayload.invoiceAmount - documentPayload.advancePayment);
            const courierGroupsForSave = buildCourierGroups(rowsForSave, courierDetails, legacyCourier);
            const payload = {
                dealId: Number(dealId),
                containers,
                document: documentPayload,
                couriers: courierGroupsForSave.map((detail) => ({
                    vesselName: detail.vesselName,
                    blNo: detail.blNo,
                    courierDate: detail.courierDate || null,
                    refNo: detail.refNo,
                    docReceiveDate: detail.docReceiveDate || null
                })),
                // Keep the original deal-level record populated for older reports.
                courier: courierGroupsForSave.length ? {
                    courierDate: courierGroupsForSave[0].courierDate || null,
                    refNo: courierGroupsForSave[0].refNo,
                    docReceiveDate: courierGroupsForSave[0].docReceiveDate || null
                } : {}
            };

            await axios.post("/shipment/add", payload);

            showToast("Shipment schedule saved successfully");
            // Stay on this page instead of navigating to the deal list. Refetch so
            // locally-added rows pick up their real ShipmentID from the database —
            // otherwise saving again would re-insert them as new rows.
            await fetchData();
            resetShipmentEditor();
            return true;

        } catch (err) {

            console.error(err);

            const message = err.response?.data?.message || "Server error";
            showToast(message);
            if (err.response?.status === 400 && String(message).toLowerCase().includes("advance payment")) {
                navigate(`/transaction/advance-payment/${dealId}`);
            }
            return false;

        }

    };

    const handleUpdateVesselAndDocument = async () => {
        if (vesselEditingIndex === null) return;
        if (!String(form.vesselName || "").trim()) {
            showToast("Vessel Name is required");
            return;
        }

        const currentRow = shipmentRows[vesselEditingIndex];
        if (!currentRow) return;

        // The page-level Edit action is deliberately limited to the fields shown
        // above. Container identity, weight, fixation and item details remain
        // unchanged; those continue to be managed in Container Details.
        const updatedRow = {
            ...currentRow,
            vesselName: form.vesselName,
            shipLine: form.shipLine,
            etd: form.etd,
            eta: form.eta,
            copyDocReceiveDate: form.copyDocReceiveDate,
            blNo: form.blNo,
            blDate: form.blDate,
            advancePayment: editingIndex === vesselEditingIndex ? form.advancePayment : currentRow.advancePayment,
            balanceAdvance: editingIndex === vesselEditingIndex ? form.balanceAdvance : currentRow.balanceAdvance,
            invoiceNo: form.invoiceNo,
            invoiceDate: form.invoiceDate,
            invoiceAmount: editingIndex === vesselEditingIndex ? form.invoiceAmount : currentRow.invoiceAmount,
            balanceAmount: editingIndex === vesselEditingIndex ? form.balanceAmount : currentRow.balanceAmount,
            freeDetentionDays: form.freeDetentionDays
        };
        const updatedRows = updateVesselDetails(shipmentRows, vesselEditingIndex, updatedRow);

        setShipmentRows(updatedRows);
        const saved = await handleSave(updatedRows, updatedRow);
        if (saved) {
            setEditingIndex(null);
            setVesselEditingIndex(null);
            setIsOtherShipLine(false);
        }
    };

    const handleAddContainer = () => {

        if (editingIndex === null && hasReachedContainerLimit) {
            showToast(`Only ${containerLimit} container(s) are allowed for this deal.`);
            return;
        }

        const validationError = validateShipmentRow(form);
        if (validationError) { showToast(validationError); return; }

        const duplicateContainerIndex = shipmentRows.findIndex((row, index) => (
            index !== editingIndex &&
            String(row.containerNo || "").trim().toLowerCase() === form.containerNo.trim().toLowerCase()
        ));
        if (duplicateContainerIndex !== -1) {
            showToast("Container No already exists in this deal");
            return;
        }

        const formRow = editingIndex !== null ? getFormShipmentRow() : getNewShipmentRow();

        if (!formRow) {
            return;
        }

        // A container can be added only while its weight fits within the
        // quantity agreed in the contract. When editing, exclude the original
        // container weight so replacing its details remains valid.
        if (Number.isFinite(contractQuantity) && contractQuantity > 0) {
            const existingQuantity = shipmentRows.reduce((total, row, index) => (
                index === editingIndex ? total : total + Number(row.netWeight || 0)
            ), 0);
            const proposedQuantity = existingQuantity + Number(formRow.netWeight || 0);

            if (proposedQuantity > contractQuantity + 0.000001) {
                showToast(
                    `Container quantity cannot exceed the contract quantity. ` +
                    `Scheduled: ${proposedQuantity.toFixed(3)}, Contract: ${contractQuantity.toFixed(3)}.`
                );
                return;
            }
        }

        if (editingIndex !== null) {

            const updated = [...shipmentRows];
            const previousContainerNo = String(updated[editingIndex]?.containerNo || "");

            const sharedRows = updateVesselDetails(updated, editingIndex, formRow);

            setShipmentRows(normalizeBLAdvances(sharedRows));
            if (previousContainerNo && previousContainerNo !== String(formRow.containerNo)) {
                setSelectedContainerNos((current) => current.map((value) => (
                    value === previousContainerNo ? String(formRow.containerNo) : value
                )));
            }
            setEditingIndex(null);

        } else {

            setShipmentRows(normalizeBLAdvances(applyBLAdvance(fillMissingVesselDetails([
                ...shipmentRows,
                formRow
            ]), formRow)));

        }

        // Only clear the container-identity fields (Rec Date, Container No, Net
        // Weight, Fixation, item details). Vessel/schedule and Document Received
        // fields are kept as-is so the next container added in this session
        // starts with the same shared details, until the user changes them.
        setForm(prev => ({
            ...emptyShipmentForm({
                advancePayment: 0,
                balanceAdvance: docForm.balanceAdvance
            }),
            advancePayment: prev.advancePayment,
            shipLine: prev.shipLine,
            vesselName: prev.vesselName,
            etd: prev.etd,
            eta: prev.eta,
            copyDocReceiveDate: prev.copyDocReceiveDate,
            blNo: prev.blNo,
            blDate: prev.blDate,
            invoiceNo: prev.invoiceNo,
            invoiceDate: prev.invoiceDate,
            invoiceAmount: prev.invoiceAmount,
            balanceAmount: prev.balanceAmount,
            freeDetentionDays: prev.freeDetentionDays
        }));

    };
    const handleShowItemDetails = () => {
        if (!form.containerNo) {
            showToast("Enter Container No first");
            return;
        }

        setForm(prev => ({
            ...prev,
            showItemDetails: true,
            itemDetails: prev.itemDetails?.length
                ? prev.itemDetails
                : createItemDetailRows(dealItems)
        }));
    };

    const handleItemDetailChange = (index, e) => {
        const { name, value } = e.target;

        setForm(prev => {
            const itemDetails = [...(prev.itemDetails || [])];
            const currentRow = itemDetails[index] || {};
            const updatedRow = {
                ...currentRow,
                [name]: value
            };

            if (name === "fixationId") {
                updatedRow.fixation = getFixationLabel(value);
            }

            itemDetails[index] = updatedRow;

            const netWeightTotal = getItemDetailNetWeightTotal(itemDetails);

            return {
                ...prev,
                itemDetails,
                netWeight: netWeightTotal ? netWeightTotal.toFixed(3) : prev.netWeight
            };
        });
    };

    const applyContainerFixations = (sourceFixations) => {
        setShipmentRows(currentRows => currentRows.map(row => {
            const containerFixation = getContainerFixation(row, sourceFixations);
            if (!containerFixation) return row;

            const fixationId = containerFixation.FixationID;
            return {
                ...row,
                fixationId,
                fixation: getFixationLabel(fixationId, "", sourceFixations),
                finalPrice: containerFixation.FinalPrice ?? "",
                itemDetails: (row.itemDetails || []).map(item => ({
                    ...item,
                    fixationId,
                    fixation: getFixationLabel(fixationId, "", sourceFixations)
                }))
            };
        }));

        setForm(currentForm => {
            const containerFixation = getContainerFixation(currentForm, sourceFixations);
            if (!containerFixation) return currentForm;

            const fixationId = containerFixation.FixationID;
            return {
                ...currentForm,
                fixationId,
                itemDetails: (currentForm.itemDetails || []).map(item => ({
                    ...item,
                    fixationId,
                    fixation: getFixationLabel(fixationId, "", sourceFixations)
                }))
            };
        });
    };

    const handleEdit = (index) => {

        const row = shipmentRows[index];
        const rowShipLine = row.shipLine || "";
        const containerFixation = fixations.find((fixation) =>
            fixationMatchesContainer(fixation, row) ||
            (fixation.ContainerNo && String(fixation.ContainerNo) === String(row.containerNo || ""))
        );
        const advanceShare = containerAdvance(row);
        const fixationInvoiceAmount = calculateFixationInvoiceAmount(row, containerFixation);

        const invoiceAmount = fixationInvoiceAmount ?? Number(row.invoiceAmount || 0);
        setIsOtherShipLine(Boolean(rowShipLine) && !isKnownShipLine(rowShipLine));

        setForm({
            recDate: row.recDate,
            containerNo: row.containerNo,
            netWeight: row.netWeight,
            fixationId: row.fixationId,
            shipLine: row.shipLine,
            copyDocReceiveDate: row.copyDocReceiveDate,
            vesselName: row.vesselName,
            etd: row.etd || "",
            eta: row.eta || "",
            blNo: row.blNo || "",
            blDate: row.blDate || "",
            advancePayment: advanceShare.toFixed(2),
            balanceAdvance: row.balanceAdvance || 0,
            invoiceNo: row.invoiceNo || "",
            invoiceDate: row.invoiceDate || "",
            invoiceAmount: fixationInvoiceAmount !== null ? invoiceAmount.toFixed(2) : (row.invoiceAmount || 0),
            invoiceAmountFromFixation: fixationInvoiceAmount !== null,
            balanceAmount: roundMoney(invoiceAmount - advanceShare).toFixed(2),
            freeDetentionDays: row.freeDetentionDays || "",
            showItemDetails: row.showItemDetails || false,
            itemDetails: row.itemDetails || []
        });

        setEditingIndex(index);
        setVesselEditingIndex(index);
        window.requestAnimationFrame(() => {
            shipmentFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        });

    };
    const handleDelete = async (index) => {
        if (deletingContainerRef.current) return;
        const row = shipmentRows[index];
        if (!row) return;
        deletingContainerRef.current = true;
        try {
            if (row.ShipmentID) {
                await axios.delete(`/shipment/${dealId}/containers/${row.ShipmentID}`);
            }
            setShipmentRows((current) => current.filter((item) => item !== row));
            setSelectedContainerNos((current) => current.filter((value) => value !== String(row.containerNo)));
            if (editingIndex === index) {
                setEditingIndex(null);
                setForm(emptyShipmentForm());
                setIsOtherShipLine(false);
            } else if (editingIndex !== null && editingIndex > index) {
                setEditingIndex(editingIndex - 1);
            }
            if (vesselEditingIndex === index) setVesselEditingIndex(null);
            else if (vesselEditingIndex !== null && vesselEditingIndex > index) setVesselEditingIndex(vesselEditingIndex - 1);
            setContainerModalBaseCount(current => index < current ? current - 1 : current);
            showToast(`Container ${row.containerNo} detail is deleted`);
        } catch (err) {
            showToast(err.response?.data?.message || "Unable to delete container detail");
        } finally {
            deletingContainerRef.current = false;
        }
    };
    useEffect(() => {

        const invoice = Number(docForm.invoiceAmount || 0);
        const advance = Number(docForm.advancePayment || 0);

        setDocForm(prev => ({
            ...prev,
            balanceAmount: invoice - advance
        }));

    }, [docForm.invoiceAmount, docForm.advancePayment]);
    const validateAdvance = () => {
        if (docForm.balanceAdvance <= 0) {
            showToast("Advance payment not completed. Shipment not allowed.");
            return;
        }
        if (docForm.balanceAdance <= 0) {
            showToast("Advance payment not completed. Shipment not allowed.");
            return;
        }
        if (docForm.advancePayment > docForm.balanceAdvance) {
            showToast("Advance cannot exceed balance");
            return false;
        }

        return true;
    };
    if (loading) return <div className="p-3">Loading...</div>;

    return (
         <>
            <DealNavigation
                dealId={dealId}
                openBuyer={() => setShowBuyer(true)}
                openShipper={() => setShowShipper(true)}
                openFixation={() => setShowFixation(true)}
                openFollowup={() => setShowFollowup(true)}
            />
        <div className="container-fluid mt-3">

            {/* ===== HEADER BADGE ===== */}
            <div className="mb-2">
                <span className="badge bg-success fs-6 px-3 py-2">
                    Shipment Schedule
                </span>
            </div>

            <DealInfoPanel
                deal={deal}
                show={showDealInfo}
                onToggle={() => setShowDealInfo(!showDealInfo)}
            />

            {/* ================= VIEW DEAL INFO ================= */}
            <div className="border rounded bg-white mb-3 d-none">

                <div
                    className="px-3 py-2 border-bottom fw-bold text-muted d-flex justify-content-between align-items-center"
                    style={{ cursor: "pointer" }}
                    onClick={() => setShowDealInfo(!showDealInfo)}
                >
                    View Deal Info
                    <span>{showDealInfo ? "▲" : "▼"}</span>
                </div>

                {showDealInfo && (
                    <div className="p-3">

                        {/* Row 1 */}
                        <div className="row g-3 mb-2">
                            <div className="col-md-4">
                                <label className="form-label fw-semibold">Deal No - Date</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={`${deal.DealID} - ${deal.DealDate?.split("T")[0]}`}
                                    disabled
                                />
                            </div>

                            <div className="col-md-4">
                                <label className="form-label fw-semibold">Status</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={deal.Status || "DOC RELEASE"}
                                    disabled
                                />
                            </div>

                            <div className="col-md-4">
                                <label className="form-label fw-semibold">S.C No - Date</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={
                                        deal.SCRNo
                                            ? `${deal.SCRNo}${deal.SCRDate ? ` - ${deal.SCRDate.split("T")[0]}` : ""}`
                                            : ""
                                    }
                                    disabled
                                />
                            </div>
                        </div>

                        {/* Row 2 */}
                        <div className="row g-3 mb-2">
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">Buyer Name</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={deal.BuyerName || ""}
                                    disabled
                                />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label fw-semibold">Shipper Name</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={deal.ShipperName || ""}
                                    disabled
                                />
                            </div>
                        </div>

                        {/* Row 3 */}
                        <div className="row g-3 mb-2">
                            <div className="col-md-2">
                                <label className="form-label fw-semibold">Shipment Period</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={deal.ShipmentDays || ""}
                                    disabled
                                />
                            </div>

                            <div className="col-md-2">
                                <label className="form-label fw-semibold">No. Of Container</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={deal.NoOfContainer || ""}
                                    disabled
                                />
                            </div>

                            <div className="col-md-2">
                                <label className="form-label fw-semibold">Total Qty</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={deal.TotalQty || ""}
                                    disabled
                                />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label fw-semibold">Material Name</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={deal.MaterialName || ""}
                                    disabled
                                />
                            </div>
                        </div>

                    </div>
                )}

            </div>

            {/* ================= SHIPMENT DETAIL ================= */}
            <div className="border rounded bg-white mb-3">

                <div
                    className="px-3 py-2 border-bottom fw-bold text-muted d-flex justify-content-between align-items-center"
                    style={{ cursor: "pointer" }}
                    onClick={() => setShowShipment(!showShipment)}
                >
                    Shipment Detail
                    <span>{showShipment ? "▲" : "▼"}</span>
                </div>

                {showShipment && (
                    <div
                        ref={shipmentFormRef}
                        className={`shipment-form-shell ${vesselEditingIndex !== null ? "is-editing" : ""}`}
                    >
                        {vesselEditingIndex !== null && (
                            <div className="shipment-edit-banner">
                                <div>
                                    <span className="shipment-edit-label">Editing shipment</span>
                                    <strong>{form.vesselName || `Container ${vesselEditingIndex + 1}`}</strong>
                                    <small>Review the highlighted details below, then save your changes.</small>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => {
                                        setEditingIndex(null);
                                        setVesselEditingIndex(null);
                                        setIsOtherShipLine(false);
                                        setForm(emptyShipmentForm({
                                            advancePayment: 0,
                                            balanceAdvance: docForm.balanceAdvance
                                        }));
                                    }}
                                >
                                    Cancel edit
                                </button>
                            </div>
                        )}

                        {/* "Container information" panel hidden for now — the "Add Container
                            Details" button now lives at the end of "Document Received" instead.
                            Kept here, commented out, in case we need to restore it. */}
                        {false && (
                            <div className="shipment-section">
                                <div className="shipment-section-title d-flex justify-content-between align-items-center gap-2">
                                    <span>Container information</span>
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-sm"
                                        onClick={() => setShowContainerModal(true)}
                                    >
                                        Add Container Details
                                    </button>
                                </div>
                                {shipmentRows.length > 0 && (
                                    <div className="text-muted small">
                                        {shipmentRows.length} container(s) added. Use "Add Container Details" to add, edit, or remove containers.
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="shipment-section">
                            <div className="shipment-section-title d-flex justify-content-between align-items-center gap-2">
                                <span>Vessel and schedule</span>
                                {/* "Apply to selected" hidden for now — kept here, commented
                                    out, in case we need to restore it. */}
                                {false && (
                                    <button type="button" className="btn btn-outline-success btn-sm" onClick={applyScheduleToSelected}>
                                        Apply to selected ({selectedContainerNos.length})
                                    </button>
                                )}
                            </div>
                        <div className="row g-3">
                            <div className="col-md-3">
                                <label className="form-label">Vessel Name <span className="text-danger">*</span></label>
                                <input
                                    className="form-control form-control-sm"
                                    required
                                    aria-required="true"
                                    value={form.vesselName}
                                    onChange={(e) => setForm({ ...form, vesselName: e.target.value })}
                                />
                            </div>

                            <div className="col-md-3">
                                <label className="form-label">Ship Line</label>
                                <select
                                    className="form-select form-select-sm"
                                    value={shipLineSelectValue}
                                    onChange={(e) => handleShipLineSelect(e.target.value)}
                                >
                                    <option value="">Select Ship Line</option>
                                    <option value={SHIP_LINE_OTHER}>Other / Add New</option>
                                    {SORTED_SHIP_LINE_OPTIONS.map((line) => (
                                        <option key={line} value={line}>
                                            {line}
                                        </option>
                                    ))}
                                </select>
                                {shipLineSelectValue === SHIP_LINE_OTHER && (
                                    <input
                                        className="form-control form-control-sm mt-2"
                                        placeholder="Enter other ship line"
                                        value={form.shipLine}
                                        onChange={(e) => setForm({ ...form, shipLine: e.target.value })}
                                    />
                                )}
                            </div>

                            <div className="col-md-3">
                                <label className="form-label">ETD</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={form.etd}
                                    onChange={(e) =>
                                        setForm({ ...form, etd: e.target.value })
                                    }
                                />
                            </div>

                            <div className="col-md-3">
                                <label className="form-label">ETA</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={form.eta}
                                    onChange={(e) =>
                                        setForm({ ...form, eta: e.target.value })
                                    }
                                />
                            </div>
                        </div>
                        </div>

                        <div className="shipment-section">
                        <div className="shipment-section-title d-flex justify-content-between align-items-center gap-2">
                            <span>Document Received</span>
                            {/* "Apply to selected" hidden for now — kept here, commented
                                out, in case we need to restore it. */}
                            {false && (
                                <button type="button" className="btn btn-outline-success btn-sm" onClick={applyDocumentToSelected}>
                                    Apply to selected ({selectedContainerNos.length})
                                </button>
                            )}
                        </div>

                        <div className="row g-3">
                            <div className="col-md">
                                <label className="form-label">Copy Doc Receive Date</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={form.copyDocReceiveDate}
                                    onChange={(e) => setForm({ ...form, copyDocReceiveDate: e.target.value })}
                                />
                            </div>

                            <div className="col-md">
                                <label className="form-label">BL No</label>
                                <input
                                    className="form-control form-control-sm"
                                    value={form.blNo}
                                    onChange={(e) => setForm({ ...form, blNo: e.target.value })}
                                />
                            </div>

                            <div className="col-md">
                                <label className="form-label">BL Date</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={form.blDate}
                                    onChange={(e) => setForm({ ...form, blDate: e.target.value })}
                                />
                            </div>

                            <div className="col-md">
                                <label className="form-label">Invoice No</label>
                                <input
                                    className="form-control form-control-sm"
                                    value={form.invoiceNo}
                                    onChange={(e) => setForm({ ...form, invoiceNo: e.target.value })}
                                />
                            </div>

                            <div className="col-md">
                                <label className="form-label">Invoice Date</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={form.invoiceDate}
                                    onChange={(e) => setForm({ ...form, invoiceDate: e.target.value })}
                                />
                            </div>

                            <div className="col-md">
                                <label className="form-label">Advance Payment</label>
                                <input
                                    type="number"
                                    className="form-control form-control-sm text-end"
                                    value={form.advancePayment}
                                    onChange={(e) =>
                                        setForm(prev => withBalanceAmount({ ...prev, advancePayment: e.target.value }))
                                    }
                                />
                            </div>
                        </div>

                        <div className="row g-3 mt-1">
                            <div className="col-md">
                                <label className="form-label">Inv. Amount</label>
                                <input
                                    type="number"
                                    className="form-control form-control-sm text-end"
                                    value={currentBLInvoice?.invoiceAmount.toFixed(2) ?? form.invoiceAmount}
                                    readOnly={Boolean(currentBLInvoice) || fixedInvoiceAmount !== null || form.invoiceAmountFromFixation}
                                    title={fixedInvoiceAmount !== null ? "Deal fixed price multiplied by shipment net weight" : form.invoiceAmountFromFixation ? "Calculated from this container's fixation final amounts" : ""}
                                    onChange={(e) =>
                                        setForm(prev => withBalanceAmount({ ...prev, invoiceAmount: e.target.value }))
                                    }
                                />
                            </div>

                            <div className="col-md">
                                <label className="form-label">Balance Amount</label>
                                <input
                                    className="form-control form-control-sm text-end"
                                    value={currentBLInvoice?.balanceAmount.toFixed(2) ?? form.balanceAmount}
                                    disabled
                                />
                            </div>

                            <div className="col-md">
                                <label className="form-label">Free Detention Days</label>
                                <input
                                    type="number"
                                    min="0"
                                    className="form-control form-control-sm"
                                    value={form.freeDetentionDays}
                                    onChange={(e) => setForm({ ...form, freeDetentionDays: e.target.value })}
                                />
                            </div>

                            {/* "Add shipment / Save changes" button hidden for now — replaced
                                by "Add Container Details" below, which opens the Container
                                Details modal (same handleAddContainer inside it). Kept here,
                                commented out, in case we need to restore it. */}
                            {false && (
                                <div className="col-md">
                                    <label className="form-label">&nbsp;</label>
                                    <button
                                        className="btn btn-success shipment-save-button w-100"
                                        onClick={handleAddContainer}
                                    >
                                        {editingIndex !== null ? "Save changes" : "Add shipment"}
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className={`mt-3 small ${advanceAllocation.pending < 0 ? "text-danger" : "text-primary"}`} role="status">
                            Advance paid: {deal.DealCurrency || "USD"} {advanceAllocation.total.toFixed(2)}
                            {" | "}Allocated by BL / vessel (including current entry): {advanceAllocation.allocated.toFixed(2)}
                            {" | "}<strong>Pending allocation: {advanceAllocation.pending.toFixed(2)}</strong>
                        </div>
                        <div className="d-flex justify-content-end gap-2 mt-2">
                            {Number.isFinite(contractQuantity) && contractQuantity > 0 && (
                                <small className="align-self-center text-muted me-auto">
                                    Scheduled Qty: {scheduledQuantity.toFixed(3)} / Contract Qty: {contractQuantity.toFixed(3)}
                                </small>
                            )}
                            <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={openContainerModal}
                                disabled={editingIndex === null && hasReachedContainerLimit}
                                title={editingIndex === null && hasReachedContainerLimit ? `This deal allows only ${containerLimit} container(s)` : undefined}
                            >
                                Add Container Details
                            </button>
                            {vesselEditingIndex !== null && (
                                <button
                                    type="button"
                                    className="btn btn-success btn-sm"
                                    onClick={handleUpdateVesselAndDocument}
                                >
                                    Update
                                </button>
                            )}
                        </div>
                        </div>

                    </div>
                )}
                {shipmentRows.length > 0 && (
                    <div className="table-responsive mt-3">
                        {/* "Apply to selected" explainer banner hidden for now — kept here,
                            commented out, in case we need to restore it. */}
                        {false && (
                            <div className="alert alert-info py-2 mb-2">
                                Select any combination of containers below, enter shared details above, and use the relevant
                                <strong> Apply to selected</strong> button. You may use different selections for schedule and BL/payment details.
                            </div>
                        )}
                        <table className="table table-bordered table-sm shipment-schedule-table">

                            <thead className="table-light text-center">
                                <tr>
                                    {/* "Select all" checkbox column hidden for now — kept here,
                                        commented out, in case we need to restore it. */}
                                    {false && (
                                        <th style={{ width: "42px" }}>
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                aria-label="Select all containers"
                                                checked={shipmentRows.length > 0 && selectedContainerNos.length === shipmentRows.length}
                                                onChange={toggleAllContainers}
                                            />
                                        </th>
                                    )}
                                    <th>Container No</th>
                                    <th>Copy Doc Receive Date</th>
                                    <th>Net Weight</th>
                                    <th>Vessel Name</th>
                                    <th>Ship Line</th>
                                    <th>BL No</th>
                                    <th>Invoice No</th>
                                    <th>Inv. Amount</th>
                                    <th>Advance Payment</th>
                                    <th>Balance Amount</th>
                                    <th>ETD</th>
                                    <th>ETA</th>
                                    <th>Action</th>
                                </tr>
                            </thead>

                            <tbody>

                                {(() => {
                                    // Group rows by Vessel Name + BL No so containers sharing both
                                    // are listed together under a header row, instead of a flat list.
                                    const groups = [];
                                    const groupIndexByKey = new Map();
                                    gridRows.forEach((row, index) => {
                                        const vesselName = String(row.vesselName || "").trim() || "(No Vessel Assigned)";
                                        const blNo = String(row.blNo || "").trim();
                                        const groupKey = `${vesselName.toLocaleUpperCase()}|${blNo.toLocaleUpperCase()}`;
                                        if (!groupIndexByKey.has(groupKey)) {
                                            groupIndexByKey.set(groupKey, groups.length);
                                            groups.push({ key: groupKey, vesselName, blNo, entries: [] });
                                        }
                                        groups[groupIndexByKey.get(groupKey)].entries.push({ row, index });
                                    });

                                    return groups.map((group) => {
                                        const totals = getShipmentTotals(group.entries.map(entry => entry.row));
                                        return (
                                        <React.Fragment key={group.key}>
                                            <tr className="table-secondary">
                                                <td colSpan="13" className="fw-semibold">
                                                    Vessel: {group.vesselName} | BL No: {group.blNo || "(No BL)"} ({group.entries.length} container{group.entries.length > 1 ? "s" : ""})
                                                </td>
                                            </tr>
                                            {group.entries.map(({ row, index }) => (
                                                <tr key={index}>
                                                    {/* per-row "select" checkbox hidden for now — kept here,
                                                        commented out, in case we need to restore it. */}
                                                    {false && (
                                                        <td className="text-center">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                aria-label={`Select container ${row.containerNo}`}
                                                                checked={isContainerSelected(row)}
                                                                onChange={() => toggleContainerSelection(row.containerNo)}
                                                            />
                                                        </td>
                                                    )}

                                                    <td>{row.containerNo}</td>
                                                    <td className="text-nowrap">{row.copyDocReceiveDate}</td>
                                                    <td>{row.netWeight}</td>
                                                    <td>{row.vesselName}</td>
                                                    <td>{row.shipLine}</td>
                                                    <td>{row.blNo}</td>
                                                    <td>{row.invoiceNo}</td>
                                                    <td className="text-end">
                                                        {row.invoiceAmount.toFixed(2)}
                                                    </td>
                                                    <td className="text-end">{containerAdvance(row).toFixed(2)}</td>
                                                    <td className="text-end">{row.balanceAmount.toFixed(2)}</td>
                                                    <td>{row.etd}</td>
                                                    <td>{row.eta}</td>

                                                    <td className="shipment-actions-cell">
                                                        <div className="shipment-row-actions">
                                                        <button
                                                            className="btn btn-sm btn-primary"
                                                            onClick={() => handleEdit(index)}
                                                        >
                                                            Edit
                                                        </button>

                                                        <button
                                                            className="btn btn-sm btn-danger"
                                                            onClick={() => handleDelete(index)}
                                                        >
                                                            Delete
                                                        </button>
                                                        </div>
                                                    </td>

                                                </tr>
                                            ))}
                                            <tr className="table-light fw-bold">
                                                <th scope="row" colSpan="7">Total</th>
                                                <td className="text-end">{totals.invoiceAmount.toFixed(2)}</td>
                                                <td className="text-end">{totals.advancePayment.toFixed(2)}</td>
                                                <td className="text-end">{totals.balanceAmount.toFixed(2)}</td>
                                                <td colSpan="3"></td>
                                            </tr>
                                        </React.Fragment>
                                        );
                                    });
                                })()}

                            </tbody>

                            <tfoot className="table-secondary fw-bold">
                                <tr>
                                    <th scope="row" colSpan="7">Grand Total</th>
                                    <td className="text-end">{gridTotals.invoiceAmount.toFixed(2)}</td>
                                    <td className="text-end">{gridTotals.advancePayment.toFixed(2)}</td>
                                    <td className="text-end">{gridTotals.balanceAmount.toFixed(2)}</td>
                                    <td colSpan="3"></td>
                                </tr>
                            </tfoot>

                        </table>
                    </div>
                )}
            </div>

            {false && (
            /* ================= DOCUMENT RECEIVED ================= */
            <div className="border rounded bg-white mb-3">

                <div
                    className="px-3 py-2 border-bottom fw-bold text-muted d-flex justify-content-between align-items-center"
                    style={{ cursor: "pointer" }}
                    onClick={() => setShowDocument(!showDocument)}
                >
                    Document Received
                    <span>{showDocument ? "▲" : "▼"}</span>
                </div>

                {showDocument && (
                    <div className="p-3">

                        <div className="row g-3">

                            <div className="col-md-3">
                                <label className="form-label">BL No</label>
                                <input
                                    className="form-control form-control-sm"
                                    value={docForm.blNo}
                                    onChange={(e) => setDocForm({ ...docForm, blNo: e.target.value })}
                                />
                            </div>

                            <div className="col-md-3">
                                <label className="form-label">BL Date</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={docForm.blDate}
                                    onChange={(e) => setDocForm({ ...docForm, blDate: e.target.value })}
                                />
                            </div>

                            <div className="col-md-3">
                                <label className="form-label">Advance Payment</label>
                                <input
                                    className="form-control form-control-sm text-end"
                                    value={docForm.advancePayment}
                                    onChange={(e) => setDocForm({ ...docForm, advancePayment: e.target.value })}
                                />
                            </div>

                            <div className="col-md-3">
                                <label className="form-label">Balance Advance</label>
                                <input
                                    className="form-control form-control-sm text-end"
                                    value={docForm.balanceAdvance}
                                    onChange={(e) => setDocForm({ ...docForm, balanceAdvance: e.target.value })}
                                />
                            </div>

                            <div className="col-md-3">
                                <label className="form-label">Invoice No</label>
                                <input
                                    className="form-control form-control-sm"
                                    value={docForm.invoiceNo}
                                    onChange={(e) => setDocForm({ ...docForm, invoiceNo: e.target.value })}
                                />
                            </div>

                            <div className="col-md-3">
                                <label className="form-label">Date</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={docForm.invoiceDate}
                                    onChange={(e) => setDocForm({ ...docForm, invoiceDate: e.target.value })}
                                />
                            </div>

                            <div className="col-md-3">
                                <label className="form-label">Inv. Amount</label>
                                <input
                                    className="form-control form-control-sm text-end"
                                    value={docForm.invoiceAmount}
                                    onChange={(e) => setDocForm({ ...docForm, invoiceAmount: e.target.value })}
                                />
                            </div>

                            <div className="col-md-3">
                                <label className="form-label">Balance Amount</label>
                                <input
                                    className="form-control form-control-sm text-end"
                                    value={docForm.balanceAmount}
                                    disabled
                                />
                            </div>

                            <div className="col-md-3">
                                <label className="form-label">Free Detention Days</label>
                                <input
                                    type="number"
                                    min="0"
                                    className="form-control form-control-sm"
                                    value={docForm.freeDetentionDays}
                                    onChange={(e) => setDocForm({ ...docForm, freeDetentionDays: e.target.value })}
                                />
                            </div>

                        </div>

                    </div>
                )}

            </div>
            )}



            {/* ================= COURIER DETAIL ================= */}
            <div className="border rounded bg-white mb-3">

                <div
                    className="px-3 py-2 border-bottom fw-bold text-muted d-flex justify-content-between align-items-center"
                    style={{ cursor: "pointer" }}
                    onClick={() => setShowCourier(!showCourier)}
                >
                    Courier Detail (Vessel / BL Wise)
                    <span>{showCourier ? "▲" : "▼"}</span>
                </div>

                {showCourier && (
                    <div className="p-3">

                        {courierGroups.length ? (
                            <div className="table-responsive">
                                <table className="table table-bordered table-sm align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Vessel Name</th>
                                            <th>BL No</th>
                                            <th>Containers</th>
                                            <th>Date</th>
                                            <th>Ref No</th>
                                            <th>Doc Rec Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {courierGroups.map((group) => (
                                            <tr key={group.key}>
                                                <td className="fw-semibold">{group.vesselName || "-"}</td>
                                                <td>{group.blNo || "-"}</td>
                                                <td className="text-center">{group.containerCount}</td>
                                                <td style={{ minWidth: "150px" }}>
                                                    <input
                                                        type="date"
                                                        aria-label={`Courier date for ${group.blNo || group.vesselName}`}
                                                        className="form-control form-control-sm"
                                                        value={group.courierDate}
                                                        onChange={(e) => updateCourierDetail(group, "courierDate", e.target.value)}
                                                    />
                                                </td>
                                                <td style={{ minWidth: "180px" }}>
                                                    <input
                                                        aria-label={`Courier reference for ${group.blNo || group.vesselName}`}
                                                        className="form-control form-control-sm"
                                                        value={group.refNo}
                                                        onChange={(e) => updateCourierDetail(group, "refNo", e.target.value)}
                                                    />
                                                </td>
                                                <td style={{ minWidth: "150px" }}>
                                                    <input
                                                        type="date"
                                                        aria-label={`Document received date for ${group.blNo || group.vesselName}`}
                                                        className="form-control form-control-sm"
                                                        value={group.docReceiveDate}
                                                        onChange={(e) => updateCourierDetail(group, "docReceiveDate", e.target.value)}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-muted small">Add shipment details to enter courier information vessel-wise or BL-wise.</div>
                        )}

                    </div>
                )}

            </div>
            {/* FOOTER BUTTONS */}
            <div className="text-end mt-3">
                <button
                    className="btn btn-success me-2"
                    onClick={() => handleSave()}
                >
                    Save
                </button>
                <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                    Cancel
                </button>
            </div>
        </div >
         <BuyerDetailModal
                show={showBuyer}
                dealId={dealId}
                onClose={() => setShowBuyer(false)}
            />
            <ShipperDetailModal
                show={showShipper}
                dealId={dealId}
                onClose={() => setShowShipper(false)}
            />
            <FollowupModal
                show={showFollowup}
                dealId={dealId}
                onClose={() => setShowFollowup(false)}
            />
            <FixationModal
                show={showFixation}
                dealId={dealId}
                totalQty={deal.TotalQty}
                dealCurrency={deal.DealCurrency}
                dealPricingDefaults={fixationPricingDefaults}
                shipmentWorksheetRows={fixationWorksheetRows}
                onClose={async () => {
                    setShowFixation(false);
                    const loadedFixations = await loadFixations();
                    applyContainerFixations(loadedFixations);
                }}
            />

            {/* ================= CONTAINER DETAILS MODAL ================= */}
            {showContainerModal && (
                <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-xl modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Container Details</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    aria-label="Close"
                                    onClick={closeContainerModal}
                                ></button>
                            </div>

                            <div className="modal-body">
                                {editingIndex !== null && (
                                    <div className="shipment-edit-banner mb-3">
                                        <div>
                                            <span className="shipment-edit-label">Editing shipment</span>
                                            <strong>{form.containerNo || `Container ${editingIndex + 1}`}</strong>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => {
                                                setEditingIndex(null);
                                                setForm(prev => ({
                                                    ...prev,
                                                    recDate: "",
                                                    containerNo: "",
                                                    netWeight: "",
                                                    fixationId: "",
                                                    itemDetails: [],
                                                    showItemDetails: false,
                                                    invoiceAmountFromFixation: false
                                                }));
                                            }}
                                        >
                                            Cancel edit
                                        </button>
                                    </div>
                                )}

                                <div className="row g-3 align-items-end">
                                    <div className="col-md-3">
                                        <label className="form-label">Rec Date *</label>
                                        <input
                                            type="date"
                                            className="form-control form-control-sm"
                                            value={form.recDate}
                                            onChange={(e) => setForm({ ...form, recDate: e.target.value })}
                                        />
                                    </div>

                                    <div className="col-md-3">
                                        <label className="form-label">Container No *</label>
                                        <div className="d-flex gap-2">
                                            <input
                                                className="form-control form-control-sm"
                                                maxLength={CONTAINER_NO_MAX_LENGTH}
                                                value={form.containerNo}
                                                onChange={(e) => setForm({
                                                    ...form,
                                                    containerNo: e.target.value.toUpperCase().slice(0, CONTAINER_NO_MAX_LENGTH)
                                                })}
                                            />
                                            {form.containerNo && (
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-primary btn-sm px-2 text-nowrap"
                                                    onClick={handleShowItemDetails}
                                                >
                                                    Add Item Details
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Direct Net Weight textbox hidden for now — Net Weight is
                                        entered per item via "Add Item Details" below and summed
                                        automatically. Kept here, commented out, in case we need
                                        to restore it. */}
                                    {false && (
                                        <div className="col-md-2">
                                            <label className="form-label">Net Weight *</label>
                                            <input
                                                type="number"
                                                className="form-control form-control-sm"
                                                value={form.netWeight}
                                                onChange={(e) => setForm({ ...form, netWeight: e.target.value })}
                                            />
                                        </div>
                                    )}

                                    <div className="col-md-3">
                                        <label className="form-label">Fixation *</label>
                                        <select
                                            className="form-select form-select-sm"
                                            value={form.fixationId}
                                            onChange={(e) => setForm({ ...form, fixationId: e.target.value })}
                                        >
                                            <option value="">Select Fixation</option>
                                            {fixations.map((f) => (
                                                <option key={f.FixationID} value={f.FixationID}>
                                                    {`${f.Fixation} | ${f.Currency || deal.DealCurrency || "USD"} ${Number(f.FinalPrice || 0).toFixed(2)}`}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Top-row Add/Update button hidden for now — the same
                                        action is available via the button in the modal footer
                                        beside "Close". Kept here, commented out, in case we
                                        need to restore it. */}
                                    {false && (
                                        <div className="col-md-1">
                                            <button
                                                type="button"
                                                className="btn btn-success btn-sm w-100"
                                                onClick={handleAddContainer}
                                            >
                                                {editingIndex !== null ? "Update" : "Add"}
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {form.showItemDetails && (
                                    <div className="table-responsive mt-3">
                                        <table className="table table-bordered table-sm align-middle mb-0" style={{ tableLayout: "fixed" }}>
                                            <thead className="table-light text-center">
                                                <tr>
                                                    <th style={{ width: "44px" }}>Sr.No</th>
                                                    <th style={{ width: "140px" }}>Item</th>
                                                    <th style={{ width: "130px" }}>Item Code</th>
                                                    <th style={{ width: "120px" }}>Qty</th>
                                                    <th style={{ width: "150px" }}>Net Weight</th>
                                                    <th style={{ width: "260px" }}>Fixation</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(form.itemDetails || []).length > 0 ? (
                                                    form.itemDetails.map((item, index) => (
                                                        <tr key={`${item.dealItemId || item.itemId || "item"}-${index}`}>
                                                            <td className="text-center">{index + 1}</td>
                                                            <td>{item.itemName || "-"}</td>
                                                            <td>{item.itemCode || "-"}</td>
                                                            <td className="text-end">{item.quantity || ""}</td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    className="form-control form-control-sm text-end"
                                                                    name="netWeight"
                                                                    value={item.netWeight || ""}
                                                                    onChange={(e) => handleItemDetailChange(index, e)}
                                                                />
                                                            </td>
                                                            <td>
                                                                <select
                                                                    className="form-select form-select-sm"
                                                                    name="fixationId"
                                                                    value={item.fixationId || ""}
                                                                    onChange={(e) => handleItemDetailChange(index, e)}
                                                                >
                                                                    <option value="">Select Fixation</option>
                                                                    {fixations.map((f) => (
                                                                        <option key={f.FixationID} value={f.FixationID}>
                                                                            {`${f.Fixation} | ${f.Currency || deal.DealCurrency || "USD"} ${Number(f.FinalPrice || 0).toFixed(2)}`}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="6" className="text-center text-muted">
                                                            No item details found for this deal.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {(() => {
                                    // Only containers added during this modal session (i.e.
                                    // added since it was last opened) are listed here —
                                    // previously-saved containers are left off this grid.
                                    const sessionRows = shipmentRows.slice(containerModalBaseCount);
                                    return (
                                        <div className="table-responsive mt-3">
                                            <table className="table table-bordered table-sm mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>ContainerNo</th>
                                                        <th>NetWeight</th>
                                                        <th>Item Weight</th>
                                                        <th>Edit</th>
                                                        <th>Delete</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {sessionRows.length > 0 ? (
                                                        sessionRows.map((row, sessionIndex) => {
                                                            const actualIndex = containerModalBaseCount + sessionIndex;
                                                            return (
                                                                <tr key={`container-modal-row-${actualIndex}`}>
                                                                    <td>{row.containerNo}</td>
                                                                    <td className="text-end">{row.netWeight}</td>
                                                                    <td className="text-end">{deal.QtyPerContainer || ""}</td>
                                                                    <td>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-link btn-sm p-0"
                                                                            onClick={() => handleEdit(actualIndex)}
                                                                        >
                                                                            Edit
                                                                        </button>
                                                                    </td>
                                                                    <td>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-link btn-sm p-0 text-danger"
                                                                            onClick={() => handleDelete(actualIndex)}
                                                                        >
                                                                            Delete
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="5" className="text-center text-muted">
                                                                No containers added yet.
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                                {sessionRows.length > 0 && (
                                                    <tfoot>
                                                        <tr>
                                                            <td className="fw-semibold">Total Weight</td>
                                                            <td className="text-end fw-semibold">
                                                                {sessionRows.reduce((sum, row) => sum + Number(row.netWeight || 0), 0).toFixed(3)}
                                                            </td>
                                                            <td colSpan="3"></td>
                                                        </tr>
                                                    </tfoot>
                                                )}
                                            </table>
                                        </div>
                                    );
                                })()}
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-success"
                                    onClick={handleAddContainer}
                                    disabled={editingIndex === null && hasReachedContainerLimit}
                                >
                                    {editingIndex !== null ? "Update" : "Add"}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={closeContainerModal}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ShipmentSchedule;

